/* eslint-disable react-hooks/rules-of-hooks */
import { tablet } from 'src/components/theme/breakpoints'
import { printPageStyle, printPseudoReportStyles, printStyle, useTypography } from 'src/components/theme/utils'
import { caption1, text2 } from 'src/components/theme/typographies'
import { textBlack, textLight, border } from 'src/components/theme/colors'
import { popper } from 'src/components/theme/levels'
import { MAP_FLOATING_WIDGET_SPACING, getMapboxBaseStyles } from 'src/components/theme/mapbox'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { css } from '@emotion/react'
import * as Shapes from 'src/components/theme/shapes'
import * as Colors from 'src/components/theme/colors'
import * as Levels from 'src/components/theme/levels'
import * as Typographies from 'src/components/theme/typographies'
import { FREEZE_VIRTUALIZED_TABLE, HIDE_WHEN_PRINT } from 'src/utils/html-to-image'

export const globalStyle = css`
  html,
  body {
    background: rgba(13, 22, 33, 1);
    @media print {
      background: white;
    }
  }
  .sb-main-padded {
    background-color: rgba(251, 251, 255, 1);
  }
  body {
    ${printStyle`
    overflow-y: scroll;
  `}
    ${printPageStyle`
    overflow-y: scroll !important;
    overflow-y: overlay !important;
    ${FANCY_SCROLLBAR}
  `}
  }
  #root {
    ${printPageStyle`
    overflow: visible !important;
  `}
  }
  body,
  #root {
    margin: 0;
    height: 100vh;
    width: 100%;
    overflow: hidden;
    ${printPageStyle`
    background: white;
    height: auto;
  `}
    ${printStyle`
    overflow: unset;
  `}
  }

  a {
    text-decoration: none;
  }

  .fancy-scrollbar {
    ${FANCY_SCROLLBAR}
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .spin {
    animation-name: spin;
    animation-duration: 1000ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }

  * {
    outline: none;
  }

  .highchart-chart-tooltip {
    padding: ${Shapes.spacing[1]}px ${Shapes.spacing[2]}px;
    border-radius: ${Shapes.borderRadius}px;
    background-color: white;
    box-shadow: ${Colors.BOX_SHADOW};
    z-index: 99999;
  }
  .highcharts-container {
    overflow: visible !important;
  }

  .place-legend {
    .place-legend-name {
      fill: ${textBlack};
      ${useTypography(text2)}
    }
    .place-legend-address {
      fill: ${textLight};
      ${useTypography(caption1)}
    }
  }

  .MuiAutocomplete-popper {
    z-index: ${popper};
  }

  // always show the clear button on the google search typeahead when dirty
  .google-auto-complete {
    &.MuiAutocomplete-root .MuiAutocomplete-clearIndicatorDirty {
      visibility: visible;
    }
  }

  #userback_button_container .userback-button-se {
    right: 160px !important;

    @media (max-width: ${tablet}px) {
      display: block !important;
      position: fixed !important;
      left: 20% !important;
      bottom: 0 !important;
      right: 20% !important;
    }
  }

  .deck-tooltip-inner {
    position: relative;
    top: ${Shapes.spacing[2]}px;
    left: ${Shapes.spacing[2]}px;
    background-color: ${Colors.bgWhite};
    border: 1px solid ${border};
    padding: ${Shapes.spacing[2]}px;
    border-radius: ${Shapes.borderRadius}px;
    box-shadow: ${Colors.BOX_SHADOW};
    color: ${Colors.textDark};
    ${useTypography(Typographies.textmd)};
  }

  ${getMapboxBaseStyles()}

  .elevioReset {
    ${printPageStyle`
      display: none !important;
    `}
  }

  ${printPseudoReportStyles()}

  .borders-layer-tooltip {
    &.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip {
      border-bottom-color: transparent;
    }

    &.mapboxgl-popup-anchor-top-left .mapboxgl-popup-content {
      border-radius: 7px;
      padding: 8px;
      ${useTypography(text2)};
    }
  }
  .mapboxgl-popup {
    max-width: 600px !important;
  }
  .mapboxgl-popup-tip {
    display: none;
  }
  .mapboxgl-popup-content {
    padding: 0 !important;
    border-radius: ${Shapes.borderRadius}px !important;
    box-shadow: ${Colors.BOX_SHADOW} !important;
    border: 1px solid ${Colors.border} !important;
    max-width: 600px !important;
    overflow: hidden;
    ${useTypography(Typographies.textmd)};
  }

  // change size of mapbox zoom in/out buttons
  .mapboxgl-ctrl {
    .mapboxgl-ctrl-zoom-out,
    .mapboxgl-ctrl-zoom-in {
      height: 32px;
    }
  }

  // this makes the zoom in/out stuff fit the same padding as the other main map widgets
  .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
    margin: 0 ${MAP_FLOATING_WIDGET_SPACING}px ${MAP_FLOATING_WIDGET_SPACING}px 0 !important;
  }

  // remove mapbox watermark
  .mapboxgl-ctrl-logo {
    display: none !important;
  }

  .no-print {
    @media print {
      display: none !important;
    }
  }

  // Used when we want to hide something in the reports before printing
  .${HIDE_WHEN_PRINT} {
    display: none !important;
  }
  // Used when we want to freeze the virtualized table before printing
  .${FREEZE_VIRTUALIZED_TABLE} {
    overflow: hidden !important;
    will-change: unset !important;
    & > div {
      position: absolute !important;
      box-sizing: content-box !important;
      & > div {
        position: relative !important;
        top: 0 !important;
        left: 0 !important;
      }
    }
  }

  #graph-popper-content {
    z-index: ${Levels.modal};
  }

  // command bar z-index hacks
  // Base overlay where everything 'lives'
  #commandbar-toast-container {
    z-index: 1010 !important;
  }

  // The container that holds the tooltip marker and content
  div[data-testid^='commandbar-tooltip-'] {
    z-index: unset !important;
  }

  // The tooltip marker that on hover shows the tooltip
  button[data-testid='commandbar-nudge-tooltip-marker'] {
    z-index: unset !important;
  }
  // The tooltip content rendered on hovering over the marker
  div[data-testid='commandbar-nudge-tooltip-content'] {
    z-index: 1 !important;
  }
`
